<template>
    <!-- Rail and sidebar first, then a column holding this mode's chrome above
         its content. The bars occupy real rows, so the content below them is
         whatever height is left. The collapsed sidebar's hover-peek is still a
         fixed overlay; that is the one exception. -->
    <div
        class="page-with-sidebar"
        :class="{
            'collapsed': ui.showSidebar === false ? true : false,
            'no-sidebar-mode': shouldHideSidebar,
            'switching-draft': isSwitchingDraft,
        }"
    >
        <NavRail
            v-if="!shouldHideSidebar"
            :groups="modeGroups"
            :active-mode="activeMode"
            :active-section-key="activeSectionKeyValue"
            @select="onModeSelect"
            @select-landing-mode="onModeLandingSelect"
        />
        <div
            ref="sidebar"
            id="sidebar"
            :class="{ 'hovering': isHovering }"
            v-if="!shouldHideSidebar && !activeSectionHasNoSubmenu"
        >
            <NavPanelHeader
                :kicker="activeGroup.label"
                :title="activeSection?.label ?? ''"
                :tag="activeSection?.tag"
            >
                <button
                    type="button"
                    class="nav-panel-toggle"
                    title="Hide sidebar"
                    @click="ui.toggleSideBar()"
                >
                    <svg width="18" height="18" fill="none" viewBox="0 0 24 24">
                        <path
                            fill="currentColor"
                            fill-rule="evenodd"
                            d="M10 7h8a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1h-8V7ZM9 7H6a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h3V7ZM4 8a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8Z"
                            clip-rule="evenodd"
                        ></path>
                    </svg>
                </button>
            </NavPanelHeader>
            <NavSideBar />
            <div
                :class="{
                    'resize-handle': true,
                    'resizing': isDragging,
                }"
                @mousedown.prevent="startResize"
            />
        </div>

        <div class="content-column">
            <!-- Draft mode: a colored bar above the top bar carries the draft's
                 title/metadata + its actions (open the thread, discard, leave,
                 apply). Supersedes the old accent-strip + logo pill. -->
            <DraftBar />

            <div
                :class="{ 'live-mode-menubar': isLiveMode }"
                class="menubar-wrapper"
            >
                <MenuBar :class="{ 'live-mode': isLiveMode }">
                    <template #start>
                        <RouterLink id="logo-route" :to="homeTarget">
                            <!-- The stek/org identity + navigation live in NavRail now,
                         so this link's own content is normally just the mode/
                         draft badges below — except on a hideSidebar page
                         (docs/help), which hides the rail entirely and so has no
                         other visible way back; show a mark there. -->
                            <GotokoLogo
                                v-if="shouldHideSidebar"
                                class="home-mark"
                            />
                            <span v-if="isLiveMode" class="live-badge"
                                >LIVE</span
                            >
                        </RouterLink>
                    </template>
                    <template #end>
                        <div class="toolbar-buttons-container">
                            <OmniBox ref="omnibox" />
                            <ActivityBar />
                            <SyncStatus
                                v-if="isModePage || failedSyncs.length"
                            />
                            <!-- Build mode renders no diagram, so entity focus and the
                         layer toggles behind this trigger would all be inert —
                         same reason EditViewButton below is hidden there. -->
                            <BusinessViewControls
                                v-if="!isLiveMode && !isBuildMode"
                                :dev="isDevMode"
                            />
                            <EditViewButton
                                v-if="
                                    isViewingLatestVersion &&
                                    stek &&
                                    !isLiveMode &&
                                    !isBuildMode
                                "
                                ref="editViewButtonRef"
                            />
                            <WhatsNewButton />
                            <!-- The unread badge follows the stek-wide stream: any stek
                                 with threads, including Run and an open draft. The
                                 "Drafts" label stays on the narrower design-scope
                                 condition (one draft at a time). Outside that, a
                                 comments button is the badge's anchor and still opens
                                 the list. Live mode has no thread drawer. -->
                            <div
                                v-if="stek && !isLiveMode && threadsEnabled"
                                class="threads-toggle-wrapper"
                            >
                                <DraftsSelector
                                    v-if="
                                        isViewingLatestVersion &&
                                        inDesignScope &&
                                        !viewingThreadBranch
                                    "
                                />
                            </div>
                        </div>
                    </template>
                </MenuBar>
            </div>

            <!-- Everything below the chrome: the scrolling page, and the
                 right-side panels that sit beside it rather than over it. -->
            <div class="content-row">
                <div class="main-container">
                    <div
                        class="page"
                        :class="{ 'page--full-bleed': isFullBleed }"
                    >
                        <Transition name="page-enter" mode="out-in">
                            <div :key="pageKey" class="page-view-wrapper">
                                <RouterView />
                            </div>
                        </Transition>
                    </div>
                </div>

                <!-- The four right panels are mutually exclusive (see
                     useExclusiveRightDrawers), and all four are siblings of the
                     page rather than children of it: they stand beside the
                     scrolling content, so none of them scrolls away with it.
                     History is the only resizable one. -->
                <div
                    v-if="showHistory"
                    class="history-sidebar"
                    :style="{
                        width: `${ui.historyWidth}px`,
                        flexBasis: `${ui.historyWidth}px`,
                    }"
                >
                    <div
                        :class="{
                            'history-resize-handle': true,
                            resizing: isHistoryDragging,
                        }"
                        @mousedown.prevent="startHistoryResize"
                    />
                    <ArtefactHistory />
                </div>
                <ThreadDrawer v-if="stek && !isLiveMode && threadsEnabled" />
                <DomainObjectDrawer v-if="stek && !isLiveMode" />
                <ProcessElementDrawer v-if="stek && !isLiveMode" />
            </div>
        </div>

        <!-- Switching the read revision reloads the tree and the page from a
             different branch, and both keep showing the old one until their
             fetches land. Cover them rather than let stale content read as
             the draft's. The rail stays clear so modes remain reachable. -->
        <Transition name="draft-switch-fade">
            <div
                v-if="isSwitchingDraft"
                class="draft-switch-overlay"
                :style="{ left: shouldHideSidebar ? '0px' : railWidthPx }"
                data-testid="draft-switch-overlay"
            >
                <div class="draft-switch-overlay__box">
                    <ProgressSpinner
                        style="width: 2.5rem; height: 2.5rem"
                        stroke-width="5"
                    />
                    <span>{{ draftSwitchLabel }}</span>
                </div>
            </div>
        </Transition>
    </div>

    <HintPopover
        v-if="currentHintStep"
        :current-step="currentHintStep"
        :is-last-step="isLastHintStep"
        @next="nextHintStep"
        @dismiss-all="dismissAllHints"
    />

    <!-- Outside .page-with-sidebar on purpose: the box appears over the page and
         owns no layout space, and nothing out here is mode-gated, so it is
         available in Design, Build and Run alike. -->
    <AskBox />
</template>

<script setup lang="ts">
import { useMouse } from "@vueuse/core";

import ProgressSpinner from "primevue/progressspinner";
import { useConfirm } from "primevue/useconfirm";
import {
    computed,
    nextTick,
    onMounted,
    onUnmounted,
    ref,
    useTemplateRef,
    watch,
} from "vue";
import {
    type RouteLocationRaw,
    type RouteRecordName,
    useRoute,
    useRouter,
} from "vue-router";

import GotokoLogo from "@/assets/gotoko-logo-mark.svg?component";
import ActivityBar from "@/components/ActivityBar.vue";
import ArtefactHistory from "@/components/ArtefactHistory.vue";
import AskBox from "@/components/AskBox.vue";
import BusinessViewControls from "@/components/BusinessViewControls.vue";
import EditViewButton from "@/components/EditViewButton.vue";
import NavPanelHeader from "@/components/NavPanelHeader.vue";
import NavRail from "@/components/NavRail.vue";
import NavSideBar from "@/components/NavSideBar.vue";
import OmniBox from "@/components/OmniBox.vue";
import ProcessElementDrawer from "@/components/Process/ProcessElementDrawer.vue";
import SyncStatus from "@/components/SyncStatus.vue";
import DraftBar from "@/components/Thread/DraftBar.vue";
import DraftsSelector from "@/components/Thread/DraftsSelector.vue";
import ThreadDrawer from "@/components/Thread/ThreadDrawer.vue";
import WhatsNewButton from "@/components/WhatsNewButton.vue";
import DomainObjectDrawer from "@/components/entities/DomainObjectDrawer.vue";
import HintPopover from "@/components/hints/HintPopover.vue";
import { clearApplicationFocus } from "@/composables/bpmn-manager/application-focus";
import { clearFocus } from "@/composables/bpmn-manager/entity-focus";
import { useLayerVisibility } from "@/composables/bpmn-manager/layer";
import { useCommandK } from "@/composables/command-k";
import { applyDraftCarry } from "@/composables/draft-carry";
import {
    draftSwitchLabel,
    isSwitchingDraft,
    useDraftSwitchTracking,
} from "@/composables/draft-switch";
import { useEditDomainTermDialog } from "@/composables/edit-domain-term-dialog";
import { useEditEntityDialog } from "@/composables/edit-entity-dialog";
import { useEditRoleDialog } from "@/composables/edit-role-dialog";
import {
    type ExclusiveDrawer,
    useExclusiveRightDrawers,
} from "@/composables/exclusive-drawers";
import { useLiveModeTooltip } from "@/composables/live-mode-tooltip";
import type { Mode, ModeGroupSection } from "@/composables/mode-navigation";
import {
    getModeGroups,
    activeSectionKey as resolveActiveSectionKey,
    resolveModeTarget,
} from "@/composables/mode-navigation";
import { useOrganizationManager } from "@/composables/organization-manager";
import { usePolicyAcceptance } from "@/composables/policy-acceptance";
import { useStekManager } from "@/composables/stek-manager";
import { useDesignScope } from "@/composables/use-design-scope";
import { usePageHints } from "@/composables/use-page-hints";
import { useThreadBranchView } from "@/composables/use-thread-branch-view";
import { CONFIRM_GROUP } from "@/lib/confirm-groups";
import { historyClosedLocation } from "@/lib/history-route";
import { withThreadRevision } from "@/lib/thread-revision-link";
import { pageKeyForRoute } from "@/router/page-key";
import {
    AuthStatus,
    useAuthenticationStore,
} from "@/stores/authentication-store";
import { useDomainObjectDrawerStore } from "@/stores/domain-object-drawer-store";
import { useHistoryManager } from "@/stores/history";
import { useLiveInsightsStore } from "@/stores/live-insights-store";
import { useLiveStore } from "@/stores/live-store";
import { useProcessElementDrawerStore } from "@/stores/process-element-drawer-store";
import { useReleaseManager, useReleaseStore } from "@/stores/release-store";
import { useSyncManager } from "@/stores/sync-store";
import { useThreadsStore } from "@/stores/threads-store";
import { useTriageStore } from "@/stores/triage-store";
import { useUIStore } from "@/stores/ui-store";
import { useUserManager } from "@/stores/user-store";

const route = useRoute();
const router = useRouter();

// Must match NavRail.vue's own width and the "left: 60px" / "- 60px" offsets
// in this file's .collapsed #sidebar rules below.
const NAV_RAIL_WIDTH = 60;
const railWidthPx = `${NAV_RAIL_WIDTH}px`;

useDraftSwitchTracking();

// Page-wrapper key (drives recreation + the page-enter transition). Element
// pages reuse the page across element->element selection so the BPMN diagram is
// not re-imported on every click — see pageKeyForRoute.
const pageKey = computed(() => pageKeyForRoute(route));
const ui = useUIStore();
const editViewButtonRef = ref<HTMLElement | null>(null);
const {
    currentStep: currentHintStep,
    isLastStep: isLastHintStep,
    nextStep: nextHintStep,
    dismissAll: dismissAllHints,
} = usePageHints(
    [
        {
            id: "edit-view-button",
            targetRef: editViewButtonRef,
            title: "Edit freely, view with confidence",
            content:
                "Use Edit mode to shape your content and View mode to see exactly how others will experience it.",
        },
    ],
    1
);
const { organization } = useOrganizationManager();
const { stek } = useStekManager();
const threadsStore = useThreadsStore();
const domainObjectDrawerStore = useDomainObjectDrawerStore();
const processElementDrawerStore = useProcessElementDrawerStore();
const triageStore = useTriageStore();
const liveInsightsStore = useLiveInsightsStore();
const liveStore = useLiveStore();
const layerVisibility = useLayerVisibility();
const { threadsEnabled, buildEnabled, externalCommunicationEnabled } =
    useUserManager();
const authStore = useAuthenticationStore();
const { checkAndShowPolicyDialog } = usePolicyAcceptance();

const { registerHotKey: registerRoleHotKey } = useEditRoleDialog();
const { registerHotKey: registerEntityHotKey } = useEditEntityDialog();
const { registerHotKey: registerTermHotKey } = useEditDomainTermDialog();

registerRoleHotKey();
registerEntityHotKey();
registerTermHotKey();

// Check for policy acceptance when user is authenticated - only on main app pages
watch(
    [() => authStore.authStatus, () => route.name],
    async ([newStatus, routeName]) => {
        if (newStatus === AuthStatus.SIGNED_IN) {
            // Only show policy dialog on main app pages, not on auth/public pages
            const mainAppRoutes: RouteRecordName[] = [
                "home",
                "organizationPage",
                "organizationHome",
                "stekPage",
                "domainPage",
                "productPage",
                "processPage",
                "profile",
                "settings",
                "application",
            ];

            if (routeName && mainAppRoutes.includes(routeName)) {
                // Small delay to ensure user data is fully loaded
                await nextTick();
                setTimeout(async () => {
                    try {
                        await checkAndShowPolicyDialog();
                    } catch (error) {
                        console.error(
                            "Error checking policy acceptance:",
                            error
                        );
                    }
                }, 500);
            }
        }
    },
    { immediate: true }
);

// Note, avoid importing 'showHistory' from the history manager. Somehow
// it will cause the route in the computed artefact in the history manager
// store to be undefined and unreactive.
const showHistory = computed(
    () => "version" in route.params && route.params.version
);

// History isn't a Pinia store like the other two — it's addressed by the
// route's ":version" param — so it's adapted to the same
// {drawerOpen, closeDrawer} shape the other panels already implement.
// closeDrawer drops the param rather than navigating away entirely, the same
// location the header's own Close button (ArtefactHistory.vue) goes to.
const historyDrawer: ExclusiveDrawer = {
    get drawerOpen() {
        return Boolean(showHistory.value);
    },
    closeDrawer: () => {
        router.push(historyClosedLocation(route));
    },
};

// All three right-side panels are kept mutually exclusive: opening any one
// closes the others.
useExclusiveRightDrawers([
    threadsStore,
    domainObjectDrawerStore,
    processElementDrawerStore,
    historyDrawer,
]);

const { isViewingLatestVersion } = useHistoryManager();
const { viewingThreadBranch } = useReleaseManager();

// Drafts are a Design-group affordance (see useDesignScope).
const { inDesignScope } = useDesignScope();
const { activateFromRoute, prepareDraftExit } = useThreadBranchView();
const confirm = useConfirm();
// One accepted "leave the stek" navigation. The repeat of that exact target
// is the only one that skips the confirm; Stay never sets it.
const acceptedDraftLeave = { path: null as string | null };
const { clearOpenThreadId } = useReleaseStore();

// Deep-link / reload restore: keep the open-thread state in sync with the
// ?thread= query param, so navigating to (or reloading) a stek URL with a thread
// id shows that draft's branch (worktree at the branch + diff indicators + the
// draft bar), and dropping the param returns to the working copy. Selecting a
// thread in the drawer (and the entity links, which carry ?thread= while a
// draft is open) sets the param; this makes it survive reloads. Deliberately
// does NOT also open the drawer — the draft bar's "Thread" button is the only
// thing that opens it now, so activating a draft (deep link, reload, or
// auto-create on edit) never pops the panel open on its own.
// Gated on threadsEnabled (single source of truth): a deep-linked ?thread= on an org
// without the threads capability must NOT activate the thread branch context (which would
// otherwise send x-thread-revision and try to read a non-existent branch). Reacts to
// threadsEnabled since capabilities load asynchronously via /user/me.
watch(
    [
        () => route.query.draft ?? route.query.thread,
        threadsEnabled,
        () => threadsStore.threads,
    ],
    ([draftId, enabled]) => {
        const threadId = typeof draftId === "string" ? draftId : undefined;
        if (enabled && threadId) {
            activateFromRoute(threadId);
        } else {
            clearOpenThreadId();
        }
    },
    { immediate: true }
);

const stopLeaveGuard = router.beforeEach((to, from) =>
    applyDraftCarry(
        to,
        from,
        viewingThreadBranch.value,
        {
            prepareDraftExit,
            confirmLeave: (onAccept) => {
                confirm.require({
                    group: CONFIRM_GROUP.dialog,
                    header: "Leave this draft?",
                    message:
                        "This leaves the draft. It stays closed until you open it again.",
                    icon: "pi pi-exclamation-triangle",
                    rejectProps: {
                        label: "Stay",
                        severity: "secondary",
                        outlined: true,
                    },
                    acceptProps: { label: "Leave draft" },
                    accept: onAccept,
                });
            },
            push: (location) => {
                void router.push(location);
            },
        },
        acceptedDraftLeave
    )
);
onUnmounted(stopLeaveGuard);

// Leaving a thread branch (the sidebar card, browser-back, a resolve — all leave
// a thread selected) auto-closes the drawer, so returning to the working copy
// doesn't leave the now-context-less panel pinned open. Exception: the drawer's
// own "back to threads list" clears the selection first, so keep the panel open
// on the list rather than closing it. Only on the true→false transition.
watch(viewingThreadBranch, (onBranch, wasOnBranch) => {
    if (wasOnBranch && !onBranch && threadsStore.drawerThreadId) {
        threadsStore.closeDrawer();
    }
});

// Load the stek's threads as soon as a stek is active AND the org has the threads
// capability, so the entity discussion badges (rendered in the sidebar tree from
// threadsStore.entityThreadMap) populate without needing to open the drawer first.
// Gated on threadsEnabled so non-VFS orgs don't hit the 403'd thread endpoints.
// Also reacts to threadsEnabled because capabilities load asynchronously via /user/me.
watch(
    [() => stek.value?.identity, threadsEnabled],
    ([stekId, enabled]) => {
        if (stekId && enabled) {
            void threadsStore.fetchThreads();
            // Stek-wide badge/notification stream — independent of whichever
            // (if any) thread the drawer/detail page has open via connectToThread.
            threadsStore.connectStekEvents(stekId);
        } else {
            threadsStore.disconnectStekEvents();
        }
    },
    { immediate: true }
);

// Load the triage issue sources while the Improvements layer is on, so the
// per-entity improvement badges (sidebar tree, page indicators, diagram dots)
// populate without visiting the Triage page first. A separate watcher from
// the threads fetch above so a layer toggle doesn't refire it; the store's
// load() is single-flight, so overlapping with the Triage page's own load
// (same stek-identity trigger) collapses to one request burst.
watch(
    [
        () => stek.value?.identity,
        externalCommunicationEnabled,
        () => layerVisibility.value.triage,
    ],
    ([stekId, enabled, triageLayerOn]) => {
        if (stekId && enabled && triageLayerOn) void triageStore.load();
    },
    { immediate: true }
);

// Full-bleed routes (the reconcile page) drop the `.page` --page-margin inset so
// their own chrome (top bar, flush left pane) reaches the content-area edges.
const isFullBleed = computed(() => route.meta.fullBleed === true);

const shouldHideSidebar = computed(() => {
    return route.meta.hideSidebar === true;
});

// Settings-style pages reached via the settings menu (see route meta
// `outsideMode`) aren't part of any of the three modes — the rail still
// renders (so the settings menu in its footer, and the org/stek switcher,
// stay reachable) but nothing in it should look active, and there's no
// submenu panel to show.
const isOutsideMode = computed(() => route.meta.outsideMode === true);

// True only on a page that's actually part of a mode (Design/Build/Run) — not
// a hideSidebar page (docs/help) and not an outsideMode one (Settings,
// Platform Admin, …). The autosave indicator (SyncStatus) reports on a stek's
// sync state, which doesn't exist on either kind of page.
const isModePage = computed(
    () => !shouldHideSidebar.value && !isOutsideMode.value
);
// Sync state is stek-scoped, not page-scoped — a sync kicked off on a mode
// page can still be failing once you've navigated to a settings page, so
// SyncStatus stays visible there too rather than hiding a real failure.
const { failedSyncs } = useSyncManager();

const currentMode = computed(() => {
    return route.matched.find((m) => m.meta.mode)?.meta.mode;
});

const isLiveMode = computed(() => currentMode.value === "live");
const isDevMode = computed(() => currentMode.value === "dev");
const isBuildMode = computed(() => currentMode.value === "build");

// Live worktree-tree needs-attention badges: one stek-scoped fetch of the
// per-entity counts while the live view is active, refreshed on the selected
// release and the live time window. Cleared when leaving live mode.
watch(
    [
        () => stek.value?.identity,
        isLiveMode,
        () => liveStore.selectedRelease,
        () => liveStore.selectedHours,
    ],
    ([stekId, live, release]) => {
        if (stekId && live) {
            void liveInsightsStore.fetch(stekId, release ?? null);
        } else {
            liveInsightsStore.clear();
        }
    },
    { immediate: true }
);

// Where the top-bar logo/home link goes: the current stek (in whichever mode
// is active), else the current org, else the last stek actually visited (see
// the lastStek watcher below) — the only way back on a hideSidebar page
// (docs/help), which hides the rail (and so NavRail's own org/stek switcher)
// entirely.
const homeTarget = computed(() => {
    if (stek.value && organization.value) {
        const params = {
            orgName: organization.value.name,
            stekName: stek.value.name,
        };
        if (isLiveMode.value) return { name: "livePage", params };
        if (isDevMode.value) return { name: "devPage", params };
        if (isBuildMode.value) return { name: "buildPage", params };
        return { name: "stekPage", params };
    }
    if (organization.value) {
        return {
            name: "organizationPage",
            params: { orgName: organization.value.name },
        };
    }
    if (ui.lastStek) return { name: "stekPage", params: ui.lastStek };
    return "/";
});

// The rail's Design/Build/Run groups. Dev availability is still the stek's own
// setting; Live is gated by subscription entitlement (not a stek setting —
// see useLiveModeTooltip) so its disabled state and tooltip come from there;
// Build is gated by the org's global `build` feature flag (see user-store.ts).
const activeMode = computed<Mode>(
    () => (currentMode.value as Mode) ?? "business"
);
const { liveModeDisabled, liveModeTooltip } = useLiveModeTooltip();
// A settings-style page (route meta `outsideMode`) can land here with no stek
// in context at all (Platform Admin, Activity Metrics, the profile Settings
// pages, Create Stek, Organization Home) — onModeSelect falls back to
// ui.lastStek there (see below), so the rows only need disabling once that
// fallback is also empty (nothing to switch modes on at all yet).
const railModeSwitchDisabled = computed(
    () => !stek.value?.name && !ui.lastStek
);
const modeGroups = computed(() => {
    const groups = getModeGroups({
        devDisabled: stek.value?.dev_mode_enabled === false,
        liveDisabled: liveModeDisabled.value,
        liveDisabledTooltip: liveModeTooltip.value,
        buildEnabled: buildEnabled.value,
    });
    if (!railModeSwitchDisabled.value) return groups;
    return groups.map((group) => ({
        ...group,
        sections: group.sections.map((section) => ({
            ...section,
            disabled: true,
            tooltip: "Open a stek to switch modes",
        })),
    }));
});
// Which row owns the panel/flyout slot — same as activeMode except on a
// Strategy page, where it's "strategy" instead of "business". No row owns it
// on an outsideMode page — it isn't part of any mode, so nothing should look
// active (see activeSectionHasNoSubmenu below).
const activeSectionKeyValue = computed(() =>
    isOutsideMode.value
        ? ""
        : resolveActiveSectionKey(
              activeMode.value,
              route.name as string | undefined
          )
);
const activeGroup = computed(
    () =>
        modeGroups.value.find((g) =>
            g.sections.some((s) => s.modeKey === activeMode.value)
        ) ?? modeGroups.value[0]
);
const activeSection = computed(() =>
    activeGroup.value.sections.find(
        (s) => s.key === activeSectionKeyValue.value
    )
);
// Overview (see ModeGroupSection.noSubmenu) has no tree/worktree of its own,
// and an outsideMode page isn't in any mode to begin with — either way there's
// no persisted #sidebar for it, not just an empty one. The rail itself still
// renders; only the panel next to it is skipped.
const activeSectionHasNoSubmenu = computed(
    () => isOutsideMode.value || activeSection.value?.noSubmenu === true
);

// Falls back to the last stek you were actually on (see the lastStek watcher
// below) when the current page has none of its own — a settings-style page
// (route meta `outsideMode`) — so switching modes from there still goes
// somewhere instead of doing nothing. Only reached as a whole pair: when the
// current org itself resolves (e.g. Organization Home, Create Stek) but its
// stek doesn't, ui.lastStek may belong to a *different* org — pairing that
// org with this one's stek (or vice versa) would silently switch into the
// wrong organization, so this falls back to lastStek only when the org is
// unknown too (mirrors homeTarget above).
const modeSwitchTarget = (): { orgName?: string; stekName?: string } => {
    if (organization.value?.name) {
        return { orgName: organization.value.name, stekName: stek.value?.name };
    }
    return ui.lastStek ?? {};
};

// An open draft belongs to the whole stek, not to Design. Mode targets carry
// no query, and dropping ?draft= is what deactivates a draft, so every mode
// — Design, Build and Run — keeps it. Leaving the stek is a separate
// decision; the navigation guard above asks before it closes the draft.
const keepDraftAcross = (to: RouteLocationRaw): RouteLocationRaw => {
    if (!viewingThreadBranch.value) return to;
    return withThreadRevision(to);
};

const onModeSelect = (section: ModeGroupSection) => {
    const { orgName, stekName } = modeSwitchTarget();
    if (!orgName || !stekName) return;
    if (section.routeName) {
        router.push(
            keepDraftAcross({
                name: section.routeName,
                params: { orgName, stekName },
            })
        );
        return;
    }
    router.push(
        keepDraftAcross(
            resolveModeTarget(
                section.modeKey,
                route.name as string | null | undefined,
                route.params,
                { orgName, stekName }
            ) as RouteLocationRaw
        )
    );
};

// Picking a group from the rail's "Change mode" popup with no mode active
// (see NavRail's noModeActive/selectLandingMode) — there's no current section
// to preserve context from, so go straight to that mode's landing page.
// Passing a null routeName to resolveModeTarget skips its entity-preserving
// lookup entirely, which is exactly the landing-page fallback we want here.
const onModeLandingSelect = (mode: Mode) => {
    const { orgName, stekName } = modeSwitchTarget();
    if (!orgName || !stekName) return;
    router.push(
        keepDraftAcross(
            resolveModeTarget(
                mode,
                null,
                {},
                {
                    orgName,
                    stekName,
                }
            ) as RouteLocationRaw
        )
    );
};

// Remembers the last stek actually visited, so a settings-style page (route
// meta `outsideMode`, where organization/stek resolve to nothing — see
// onModeSelect and homeTarget) still has somewhere to send you back to.
watch(
    [() => organization.value?.name, () => stek.value?.name],
    ([orgName, stekName]) => {
        if (orgName && stekName) ui.lastStek = { orgName, stekName };
    },
    { immediate: true }
);

// Clear focus when dev mode is disabled, stek switches, or domain context changes.
const clearAllFocus = () => {
    clearFocus();
    clearApplicationFocus();
};
watch(
    () => stek.value?.dev_mode_enabled,
    (enabled) => {
        if (enabled === false) clearAllFocus();
    }
);
watch(
    () => stek.value?.identity,
    (id, prevId) => {
        if (prevId !== undefined && id !== prevId) clearAllFocus();
    }
);
watch(
    () => route.params.domainName,
    (domain, prevDomain) => {
        if (prevDomain !== undefined && domain !== prevDomain) clearAllFocus();
    }
);

// Live mode styling is handled through CSS variables/classes to avoid inconsistencies.
// JS only toggles a single class on <body>.
watch(
    isLiveMode,
    (isLive) => {
        nextTick(() => {
            document.body.classList.toggle("live-mode-active", isLive);
        });
    },
    { immediate: true }
);

// Reflects the active rail group (design/build/run) onto <body> so
// --stekz-primary-color (see _variables.scss) — and anything else styled
// off it — tracks whichever mode the user is currently in, app-wide.
watch(
    () => activeGroup.value.id,
    (groupId) => {
        nextTick(() => {
            document.body.setAttribute("data-mode-group", groupId);
        });
    },
    { immediate: true }
);

const sidebar = useTemplateRef<HTMLElement>("sidebar");

// ⌘K has exactly one registration, and it decides which global surface opens —
// the ask box where it is available, search otherwise. See composables/command-k.ts.
const omnibox = useTemplateRef<{ openSearch: () => void }>("omnibox");
useCommandK(() => omnibox.value?.openSearch());

const isHovering = ref(false);
const isDragging = ref(false);
const { x } = useMouse();

const startResize = () => {
    isDragging.value = true;
    document.addEventListener("mousemove", handleResize);
    document.addEventListener("mouseup", stopResize);
};

const handleResize = (e: MouseEvent) => {
    if (isDragging.value && sidebar.value) {
        const newWidth = e.pageX;
        if (newWidth >= 120 && newWidth <= 500) {
            sidebar.value.style.width = `${newWidth}px`;
            sidebar.value.style.flexBasis = `${newWidth}px`;
            ui.sidebarWidth = newWidth;
        }
    }
};

const stopResize = () => {
    isDragging.value = false;
    document.removeEventListener("mousemove", handleResize);
    document.removeEventListener("mouseup", stopResize);
};

// History panel resize — it's on the right, so its width grows as the mouse
// moves left (width = viewport width minus the cursor's x). Persisted via
// ui.historyWidth; the panel binds it inline, so no imperative style needed.
const isHistoryDragging = ref(false);

const startHistoryResize = () => {
    isHistoryDragging.value = true;
    document.addEventListener("mousemove", handleHistoryResize);
    document.addEventListener("mouseup", stopHistoryResize);
};

const handleHistoryResize = (e: MouseEvent) => {
    if (!isHistoryDragging.value) return;
    const newWidth = window.innerWidth - e.pageX;
    if (newWidth >= 260 && newWidth <= 800) {
        ui.historyWidth = newWidth;
    }
};

const stopHistoryResize = () => {
    isHistoryDragging.value = false;
    document.removeEventListener("mousemove", handleHistoryResize);
    document.removeEventListener("mouseup", stopHistoryResize);
};

watch(x, () => {
    // if the sidebar is collapsed, make it visible (but still collapsed).
    // The permanent 60px rail now sits before the sidebar, so the reveal
    // edge is just past the rail rather than the screen's own edge.
    if (x.value < NAV_RAIL_WIDTH + 5 && !ui.showSidebar) {
        isHovering.value = true;
    }
    // Turn hovering off when mouse x leaves sidebar
    if (
        !isDragging.value &&
        isHovering.value &&
        x.value > NAV_RAIL_WIDTH + ui.sidebarWidth + 10
    ) {
        isHovering.value = false;
    }
});

onMounted(() => {
    updateSidebarWidth();
});

// Watch for changes to the shouldHideSidebar value
watch(
    [shouldHideSidebar, sidebar],
    () => {
        if (!shouldHideSidebar.value && sidebar.value) {
            // Use a nextTick to ensure the sidebar element is in the DOM
            nextTick(() => {
                updateSidebarWidth();
            });
        }
    },
    { immediate: true }
);

const updateSidebarWidth = () => {
    if (sidebar.value) {
        sidebar.value.style.width = `${ui.sidebarWidth}px`;
        sidebar.value.style.flexBasis = `${ui.sidebarWidth}px`;
    }
};

onUnmounted(() => {
    threadsStore.disconnectStekEvents();
});
</script>

<style scoped lang="scss">
// A row of the content column, not an overlay. Positioned only so the bar's
// own non-teleported children can paint over the page below it.
.menubar-wrapper {
    flex: 0 0 auto;
    position: relative;
    z-index: 10;
}

.p-menubar {
    // Once .p-menubar-start (flex-shrink: 0 below) and .p-menubar-end no longer
    // fit on one line, wrap the whole end slot onto its own row below start
    // instead of squeezing everything onto one line. .p-menubar-end's own
    // margin-left: auto (PrimeVue's base theme) still right-aligns it once it's
    // alone on that second row.
    flex-wrap: wrap;

    :deep(.p-menubar-start) {
        overflow-x: auto;
        // PrimeVue's own responsive breakpoint (~960px, independent of this app's
        // 768px convention) adds a `p-menubar-mobile` class that changes the
        // menubar's internal flex distribution. That can squeeze this slot's
        // flex-basis all the way to 0 to make room for other menubar content,
        // taking the sidebar toggle button down with it (not a visibility/opacity
        // issue — the button's own box shrinks to zero width). Force the slot to
        // never shrink below the width its content actually needs so the toggle
        // button can't be squeezed away purely by viewport width.
        flex-shrink: 0;
        min-width: max-content;
    }

    :deep(.p-menubar-end) {
        display: flex;
        gap: 0.5em;
    }
    // min-height, not height: the bar grows to a second row once the toolbar
    // buttons wrap, and the column below it simply gets that much shorter.
    min-height: var(--menu-bar-base-height);
}

.threads-toggle-wrapper {
    position: relative;
    display: inline-flex;
}

.threads-unread-badge {
    position: absolute;
    top: -4px;
    right: -4px;
    pointer-events: none;
    transform: scale(0.85);
    transform-origin: top right;
}

// Keep header text styling the same in design & live; only the LIVE badge differs.

.nav-panel-toggle {
    width: 26px;
    height: 26px;
    flex: 0 0 auto;
    display: grid;
    place-items: center;
    border: none;
    background: none;
    color: var(--text-color-secondary);
    cursor: pointer;
    transition:
        color 0.14s,
        background 0.14s;

    &:hover {
        color: var(--text-color);
        background: var(--surface-background-mild);
    }
}

// Fixed rather than a child of the scrolling .main-container, so it covers the
// tree beside the chrome as well as the page below it.
.draft-switch-overlay {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    z-index: 900;
    display: flex;
    align-items: center;
    justify-content: center;
    background: color-mix(
        in srgb,
        var(--p-content-background) 72%,
        transparent
    );

    &__box {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 0.75rem;
        font-size: 0.9rem;
        font-weight: 500;
        color: var(--text-color-secondary);
    }
}

.draft-switch-fade-enter-active,
.draft-switch-fade-leave-active {
    transition: opacity 120ms ease;
}

.draft-switch-fade-enter-from,
.draft-switch-fade-leave-to {
    opacity: 0;
}
</style>

<style lang="scss">
:root {
    --draft-bar-height: 40px;
}

// Global styles for live mode - override PrimeVue CSS variables
// NOTE: Avoid global "force white" overrides here.
// They made the stek title/logo invisible when the menubar background wasn't actually in live mode.

// (Removed) live-mode gradient + forced colors to keep header consistent with design mode.

a#logo-route {
    display: flex;
    flex-direction: row;
    gap: 0.5rem;
    align-items: center;
    justify-content: center;
    padding-left: 8px;
    padding-right: 8px;
    text-decoration: none;
    text-decoration: inherit;
    visibility: visible !important;
    opacity: 1 !important;

    .home-mark {
        width: 22px;
        height: 22px;
        color: var(--text-color);
    }

    @keyframes live-pulse {
        0%,
        100% {
            opacity: 1;
            transform: scale(1);
        }
        50% {
            opacity: 0.3;
            transform: scale(0.75);
        }
    }

    .live-badge {
        display: inline-flex;
        align-items: center;
        gap: 0.35em;
        font-size: 0.6em;
        font-weight: 700;
        padding: 0.2em 0.5em;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        color: #d32f2f; /* red */
        border: 1px solid #d32f2f;
        background: transparent;

        &::before {
            content: "";
            display: block;
            width: 0.55em;
            height: 0.55em;
            border-radius: 50%;
            background-color: #d32f2f;
            animation: live-pulse 1.4s ease-in-out infinite;
            flex-shrink: 0;
        }
    }
}

// Keep logo/title colors consistent across modes; LIVE badge is handled above.

// The app is exactly the viewport: a row of rail, sidebar and the content
// column, which then stacks this mode's bars above the scrolling page. Nothing
// overlaps, so nothing needs to know how tall anything else is.
.page-with-sidebar {
    display: flex;
    flex-direction: row;
    height: 100vh;
    overflow: hidden;

    .content-column {
        display: flex;
        flex-direction: column;
        flex: 1 1 auto;
        // Without these a wide page or a tall one makes the column itself grow
        // instead of letting .main-container scroll inside it.
        min-width: 0;
        min-height: 0;
    }

    // The bars stay above the draft-switch overlay, so the draft bar's Leave
    // stays clickable if a fetch drags on. Only then: raised for good they
    // would cover the collapsed sidebar's hover-peek.
    &.switching-draft .content-column > :not(.content-row) {
        z-index: 901;
    }

    .content-row {
        display: flex;
        flex-direction: row;
        flex: 1 1 auto;
        min-height: 0;
    }

    // The rail is never part of the collapse/resize column — it always stays
    // in the flex flow at a fixed 60px, so mode-switching remains reachable
    // even while the tree panel is collapsed (NavRail.vue sizes its own
    // height; nothing else here needs to size it).

    &.collapsed {
        #sidebar {
            // A hover-peek reveal is a temporary overlay: taken out of flow so
            // the content column keeps the width it has while collapsed, and
            // above the chrome it slides over.
            z-index: 1002;
            position: fixed;
            // Sit to the right of the permanent rail instead of flush left,
            // so the hover-revealed panel doesn't cover the rail.
            left: 60px;

            // Hidden state must clear both its own width AND the rail's
            // 60px offset above, or a sliver of it keeps bleeding back into
            // view right on top of the rail.
            &:not(.hovering) {
                transform: translateX(calc(-100% - 60px));
            }
        }
    }

    &.no-sidebar-mode {
        .main-container {
            padding-left: 0;
            margin-left: 0;
        }
    }

    #sidebar {
        // 100vh rather than 100%: while collapsed it is fixed, so it has no
        // parent box to fill.
        height: 100vh;
        background: var(--p-menubar-background);
        border-right: 2px solid var(--surface-border, #dee2e6);
        display: flex;
        flex-direction: column;
        flex-grow: 0;
        flex-shrink: 0;
        left: 0;
        min-width: 120px;
        max-width: 500px;
        overflow-y: hidden; // note we need this for the default resize handle
        position: relative; /* Needed for absolute positioning of handle */
        transform: translateX(0);
        transition-property: transform;
        transition-timing-function: ease;
        transition-duration: 200ms;

        &.on-top {
            z-index: 600;
            overflow: visible;
        }
    }

    // The app's scroll container. It is a flex item of .content-row, so its
    // height is simply what the bars left over — no viewport arithmetic, and
    // the scrollbar starts at its own top edge rather than behind a bar.
    .main-container {
        position: relative;
        width: 100%;
        min-width: 0;
        min-height: 0;
        overflow-y: auto;
        // The gutter stays reserved, or content jumps sideways by the
        // scrollbar's width on every move between a page that scrolls and one
        // that doesn't.
        scrollbar-gutter: stable;
        flex-grow: 1;
        display: flex;
        flex-direction: column;

        // The nav sidebar (up to 500px) and History (up to 800px) resize
        // independently, so both dragged to their max on a <=1440px screen
        // would otherwise squeeze the page down to its padding alone. Past
        // this floor .main-container's own overflow-x takes over instead of
        // the content becoming unreadable.
        > .page {
            min-width: 320px;
        }
    }

    // The resizable right panel, and the only one of the four that isn't its
    // own component. Same shape as .thread-panel and friends: a flex item of
    // .content-row, so it is already exactly as tall as the page area and
    // sizes from its parent rather than from the viewport. `position:
    // relative` anchors its own resize handle.
    .history-sidebar {
        position: relative;
        flex: 0 0 auto;
        min-width: 260px;
        max-width: 800px;
        min-height: 0;
        display: flex;
        flex-direction: column;
        overflow: hidden;
        border-left: 1px solid var(--palette-grey-300);
    }
}

.page {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    padding: calc(var(--page-margin) * 0.5) var(--page-margin);
    background: var(--p-content-background);
}

/* Full-bleed routes opt out of the page inset (see route meta `fullBleed`) so a
   page with its own top bar + edge-anchored panes fills the content area. */
.page.page--full-bleed {
    padding: 0;
}

.resize-handle {
    position: absolute;
    top: 0;
    right: 0;
    width: 3px;
    height: 100%;
    background: transparent;
    cursor: col-resize;
    transition: 100ms 50ms ease-in-out;

    &:hover,
    &.resizing {
        background: var(--stekz-primary-background);
    }
}

// History panel handle sits on the panel's LEFT edge (the panel is on the right).
.history-resize-handle {
    position: absolute;
    top: 0;
    left: 0;
    width: 3px;
    height: 100%;
    background: transparent;
    cursor: col-resize;
    z-index: 1;
    transition: 100ms 50ms ease-in-out;

    &:hover,
    &.resizing {
        background: var(--stekz-primary-background);
    }
}

.popover-container {
    .info-text {
        padding: 1rem;
        text-align: center;
    }

    :deep(.p-datatable) {
        .sync-action-buttons {
            display: flex;
            gap: 8px;
        }
    }
}

.toolbar-buttons-container {
    display: flex;
    gap: 0.5em;
}

/* Wrapper that holds RouterView inside the page transition — must fill available space */
.page-view-wrapper {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    min-height: 0;
    min-width: 0;
}

/* Page enter transition — subtle fade + slide-up on every route change */
.page-enter-enter-active {
    transition:
        opacity 160ms ease-out,
        transform 160ms ease-out;
}
.page-enter-leave-active {
    transition: opacity 80ms ease-in;
}
.page-enter-enter-from {
    opacity: 0;
    transform: translateY(6px);
}
.page-enter-leave-to {
    opacity: 0;
}
</style>
